import type { RouteAccess } from '@/access';

const STORAGE_KEY_PREFIX = 'fg:frontend:last-visited-route:v1';
const RECENT_STORAGE_KEY_PREFIX = 'fg:frontend:recent-routes:v1';
const FAVORITE_STORAGE_KEY_PREFIX = 'fg:frontend:favorite-routes:v1';
const INTERNAL_ORIGIN = 'https://internal.flashgreen.local';
const MAX_RECENT_ROUTES = 10;
const MAX_FAVORITE_ROUTES = 12;

export const NAVIGATION_PREFERENCES_CHANGED_EVENT = 'fg:navigation-preferences-changed';

export interface RestorableRouteConfig {
  path?: string;
  name?: string;
  redirect?: string;
  access?: string;
  component?: string;
  element?: unknown;
  layout?: boolean;
  hideInMenu?: boolean;
  routes?: RestorableRouteConfig[];
  children?: RestorableRouteConfig[];
}

export interface NavigationRoute {
  path: string;
  nameChain: string[];
}

interface StoredRoute {
  version: 1;
  path: string;
  savedAt: number;
}

interface StoredRouteList {
  version: 1;
  items: StoredRoute[];
}

interface NormalizedInternalRoute {
  pathname: string;
  path: string;
}

const getUserStorageKey = (userId: unknown): string | null => {
  const normalizedUserId = String(userId ?? '').trim();
  return normalizedUserId ? `${STORAGE_KEY_PREFIX}:${encodeURIComponent(normalizedUserId)}` : null;
};

const getUserScopedStorageKey = (prefix: string, userId: unknown): string | null => {
  const normalizedUserId = String(userId ?? '').trim();
  return normalizedUserId ? `${prefix}:${encodeURIComponent(normalizedUserId)}` : null;
};

const normalizeInternalRoute = (rawPath: string): NormalizedInternalRoute | null => {
  try {
    const parsed = new URL(rawPath, INTERNAL_ORIGIN);
    if (parsed.origin !== INTERNAL_ORIGIN || !parsed.pathname.startsWith('/')) {
      return null;
    }

    const pathname = parsed.pathname.replace(/\/{2,}/g, '/').replace(/\/$/, '') || '/';
    return {
      pathname,
      path: `${pathname}${parsed.search}${parsed.hash}`,
    };
  } catch {
    return null;
  }
};

const normalizeInternalPathname = (rawPath: string): string | null =>
  normalizeInternalRoute(rawPath)?.pathname ?? null;

const routePatternMatches = (pattern: string | undefined, pathname: string): boolean => {
  if (!pattern || pattern === '*' || pattern.includes('*')) {
    return false;
  }

  const normalizedPattern = pattern.replace(/\/$/, '') || '/';
  if (normalizedPattern === '/') {
    return pathname === '/';
  }

  const patternSegments = normalizedPattern.split('/').filter(Boolean);
  const pathnameSegments = pathname.split('/').filter(Boolean);

  if (patternSegments.length !== pathnameSegments.length) {
    return false;
  }

  return patternSegments.every((segment, index) => {
    if (segment.startsWith(':')) {
      return pathnameSegments[index].length > 0;
    }

    return segment === pathnameSegments[index];
  });
};

const isRouteChainAccessible = (chain: RestorableRouteConfig[], access: RouteAccess): boolean => {
  return chain.every((route) => {
    if (route.layout === false) {
      return false;
    }

    return !route.access || access[route.access] === true;
  });
};

const isRestorableLeaf = (route: RestorableRouteConfig): boolean => {
  if (route.redirect || (!route.component && !route.element)) {
    return false;
  }

  return typeof route.component !== 'string' || !route.component.includes('Redirect');
};

const hasStaticPath = (
  route: RestorableRouteConfig,
): route is RestorableRouteConfig & {
  path: string;
} => {
  return Boolean(route.path && !route.path.includes(':') && !route.path.includes('*'));
};

const isNamedNavigationTarget = (
  route: RestorableRouteConfig,
): route is RestorableRouteConfig & { path: string; name: string } => {
  if (!route.name || route.hideInMenu || !hasStaticPath(route)) {
    return false;
  }

  if (isRestorableLeaf(route)) {
    return true;
  }

  const nestedRoutes = [...(route.routes || []), ...(route.children || [])];
  return nestedRoutes.some((child) => child.path === route.path && isRestorableLeaf(child));
};

const findRestorableRouteChain = (
  routes: RestorableRouteConfig[],
  pathname: string,
  ancestors: RestorableRouteConfig[] = [],
): RestorableRouteConfig[] | null => {
  let bestMatch: RestorableRouteConfig[] | null = null;

  for (const route of routes) {
    const chain = [...ancestors, route];
    const nestedRoutes = [...(route.routes || []), ...(route.children || [])];

    if (nestedRoutes.length > 0) {
      const nestedMatch = findRestorableRouteChain(nestedRoutes, pathname, chain);
      if (nestedMatch && (!bestMatch || nestedMatch.length > bestMatch.length)) {
        bestMatch = nestedMatch;
      }
    }

    if (
      routePatternMatches(route.path, pathname) &&
      isRestorableLeaf(route) &&
      (!bestMatch || chain.length > bestMatch.length)
    ) {
      bestMatch = chain;
    }
  }

  return bestMatch;
};

export const isRestorableRoutePath = (
  rawPath: string,
  access: RouteAccess,
  routes: RestorableRouteConfig[],
): boolean => {
  const pathname = normalizeInternalPathname(rawPath);
  if (!pathname || pathname === '/') {
    return false;
  }

  const routeChain = findRestorableRouteChain(routes, pathname);
  return Boolean(routeChain && isRouteChainAccessible(routeChain, access));
};

export const getAccessibleNavigationRoutes = (
  access: RouteAccess,
  routes: RestorableRouteConfig[],
): NavigationRoute[] => {
  const targets = new Map<string, NavigationRoute>();

  const visit = (
    items: RestorableRouteConfig[],
    ancestors: RestorableRouteConfig[] = [],
    ancestorNames: string[] = [],
  ) => {
    for (const route of items) {
      const chain = [...ancestors, route];
      const nameChain = route.name ? [...ancestorNames, route.name] : ancestorNames;

      if (
        isNamedNavigationTarget(route) &&
        isRouteChainAccessible(chain, access) &&
        !targets.has(route.path)
      ) {
        targets.set(route.path, { path: route.path, nameChain });
      }

      const nestedRoutes = [...(route.routes || []), ...(route.children || [])];
      if (nestedRoutes.length > 0) {
        visit(nestedRoutes, chain, nameChain);
      }
    }
  };

  visit(routes);
  return [...targets.values()];
};

const readStoredRouteList = (storageKey: string): StoredRoute[] => {
  try {
    const rawValue = window.localStorage.getItem(storageKey);
    if (!rawValue) {
      return [];
    }

    const parsed = JSON.parse(rawValue) as Partial<StoredRouteList>;
    if (parsed.version !== 1 || !Array.isArray(parsed.items)) {
      window.localStorage.removeItem(storageKey);
      return [];
    }

    return parsed.items.filter(
      (item): item is StoredRoute =>
        item?.version === 1 && typeof item.path === 'string' && typeof item.savedAt === 'number',
    );
  } catch {
    window.localStorage.removeItem(storageKey);
    return [];
  }
};

const writeStoredRouteList = (storageKey: string, items: StoredRoute[], notify = true): boolean => {
  try {
    const value: StoredRouteList = { version: 1, items };
    window.localStorage.setItem(storageKey, JSON.stringify(value));
    if (notify) {
      window.dispatchEvent(new CustomEvent(NAVIGATION_PREFERENCES_CHANGED_EVENT));
    }
    return true;
  } catch {
    return false;
  }
};

const rememberRecentRoute = (userId: unknown, pathname: string): void => {
  const storageKey = getUserScopedStorageKey(RECENT_STORAGE_KEY_PREFIX, userId);
  if (!storageKey) {
    return;
  }

  const nextItem: StoredRoute = { version: 1, path: pathname, savedAt: Date.now() };
  const items = readStoredRouteList(storageKey).filter((item) => item.path !== pathname);
  writeStoredRouteList(storageKey, [nextItem, ...items].slice(0, MAX_RECENT_ROUTES));
};

export const rememberLastVisitedRoute = (
  userId: unknown,
  rawPath: string,
  access: RouteAccess,
  routes: RestorableRouteConfig[],
): boolean => {
  if (typeof window === 'undefined') {
    return false;
  }

  const storageKey = getUserStorageKey(userId);
  const normalizedRoute = normalizeInternalRoute(rawPath);
  if (
    !storageKey ||
    !normalizedRoute ||
    !isRestorableRoutePath(normalizedRoute.pathname, access, routes)
  ) {
    return false;
  }

  const storedRoute: StoredRoute = {
    version: 1,
    path: normalizedRoute.path,
    savedAt: Date.now(),
  };

  try {
    window.localStorage.setItem(storageKey, JSON.stringify(storedRoute));
    rememberRecentRoute(userId, normalizedRoute.pathname);
    return true;
  } catch {
    return false;
  }
};

export const getRecentVisitedRoutes = (
  userId: unknown,
  access: RouteAccess,
  routes: RestorableRouteConfig[],
  options: { excludePath?: string; limit?: number } = {},
): string[] => {
  if (typeof window === 'undefined') {
    return [];
  }

  const storageKey = getUserScopedStorageKey(RECENT_STORAGE_KEY_PREFIX, userId);
  if (!storageKey) {
    return [];
  }

  const excludedPath = options.excludePath ? normalizeInternalPathname(options.excludePath) : null;
  const storedItems = readStoredRouteList(storageKey);
  const validItems = storedItems.filter((item) => {
    const pathname = normalizeInternalPathname(item.path);
    return Boolean(pathname && isRestorableRoutePath(pathname, access, routes));
  });

  if (validItems.length !== storedItems.length) {
    writeStoredRouteList(storageKey, validItems, false);
  }

  return validItems
    .filter((item) => item.path !== excludedPath)
    .slice(0, options.limit ?? MAX_RECENT_ROUTES)
    .map((item) => item.path);
};

export const getFavoriteRoutes = (
  userId: unknown,
  access: RouteAccess,
  routes: RestorableRouteConfig[],
): string[] => {
  if (typeof window === 'undefined') {
    return [];
  }

  const storageKey = getUserScopedStorageKey(FAVORITE_STORAGE_KEY_PREFIX, userId);
  if (!storageKey) {
    return [];
  }

  const navigationPaths = new Set(
    getAccessibleNavigationRoutes(access, routes).map((route) => route.path),
  );
  const storedItems = readStoredRouteList(storageKey);
  const validItems = storedItems.filter((item) => navigationPaths.has(item.path));

  if (validItems.length !== storedItems.length) {
    writeStoredRouteList(storageKey, validItems, false);
  }

  return validItems.map((item) => item.path);
};

export const toggleFavoriteRoute = (
  userId: unknown,
  rawPath: string,
  access: RouteAccess,
  routes: RestorableRouteConfig[],
): boolean | null => {
  if (typeof window === 'undefined') {
    return null;
  }

  const storageKey = getUserScopedStorageKey(FAVORITE_STORAGE_KEY_PREFIX, userId);
  const pathname = normalizeInternalPathname(rawPath);
  const navigationPaths = new Set(
    getAccessibleNavigationRoutes(access, routes).map((route) => route.path),
  );

  if (!storageKey || !pathname || !navigationPaths.has(pathname)) {
    return null;
  }

  const items = readStoredRouteList(storageKey);
  const isFavorite = items.some((item) => item.path === pathname);
  const nextItems = isFavorite
    ? items.filter((item) => item.path !== pathname)
    : [
        { version: 1 as const, path: pathname, savedAt: Date.now() },
        ...items.filter((item) => item.path !== pathname),
      ].slice(0, MAX_FAVORITE_ROUTES);

  return writeStoredRouteList(storageKey, nextItems) ? !isFavorite : null;
};

export const getLastVisitedRoute = (
  userId: unknown,
  access: RouteAccess,
  routes: RestorableRouteConfig[],
): string | null => {
  if (typeof window === 'undefined') {
    return null;
  }

  const storageKey = getUserStorageKey(userId);
  if (!storageKey) {
    return null;
  }

  try {
    const rawValue = window.localStorage.getItem(storageKey);
    if (!rawValue) {
      return null;
    }

    const storedRoute = JSON.parse(rawValue) as Partial<StoredRoute>;
    const normalizedRoute =
      storedRoute.version === 1 && typeof storedRoute.path === 'string'
        ? normalizeInternalRoute(storedRoute.path)
        : null;

    if (
      !normalizedRoute ||
      !isRestorableRoutePath(normalizedRoute.pathname, access, routes)
    ) {
      window.localStorage.removeItem(storageKey);
      return null;
    }

    return normalizedRoute.path;
  } catch {
    window.localStorage.removeItem(storageKey);
    return null;
  }
};
